/**
 * Sollie — keep a sparse option grid navigable.
 *
 * WHY THIS EXISTS
 * Shopify presents a multi-option product as the full cross-product of its
 * option values, but suppliers build only some of the pairs. 39 products in
 * this catalog have a grid bigger than their variant list — the two-tone
 * pendant offers 9 x 9 = 81 combinations and has 13 real variants.
 *
 * The picker shows only the combinations that exist (see
 * snippets/sollie-v3-variant-picker.liquid), which is what a configurator
 * should do. That alone leaves one dead end: the shopper changes step 1 while
 * step 2 still holds a value the new step-1 value has never been paired with.
 * Horizon builds its request from whatever radios are checked
 * (`querySelectorAll('fieldset input:checked')`), Shopify cannot resolve that
 * pair, and add to cart renders "Unavailable" — a cul-de-sac on a product where
 * nothing is out of stock. Measured before this file existed: 24 of 32 option
 * clicks across five sparse products ended there.
 *
 * WHAT IT DOES
 * Runs on the CAPTURE phase, so it lands before variant-picker's own change
 * listener, and reconciles the other steps to a combination that actually
 * exists before the request is built. The shopper never sees the invalid state.
 *
 * Which combination it picks, in priority order:
 *   1. the value just clicked — always kept
 *   2. the steps BEFORE it — an earlier step is the more deliberate choice
 *      ("I want the black body"), so it is preserved where possible
 *   3. as many of the later steps as still fit
 *
 * It also stamps the resolved variant id onto the clicked radio, because
 * Horizon reads `dataset.variantId` off that element to keep `?variant=` in the
 * address bar; without it a reconciled selection would vanish from a shared or
 * reloaded URL.
 *
 * DETAILS THAT MATTER
 * - Combination data is re-read from the `script[data-sv3-combos]` blob on every
 *   event, so it stays correct across morphs. Its type is deliberately NOT
 *   `application/json`: Horizon locates the variant payload with
 *   `querySelector('variant-picker script[type="application/json"]')` and takes
 *   the first match, so a second JSON script inside the picker would shadow it
 *   and kill every variant update.
 * - Steps are keyed by `data-fieldset-index`, which is the option's position on
 *   the product, not the fieldset's position on screen. A single-value option
 *   renders as a line of text with no fieldset at all, so those two numbers
 *   diverge and matching by screen order would map values onto the wrong step.
 *   A position with no fieldset is treated as a wildcard — it cannot constrain
 *   anything, because every variant carries the same value there.
 * - Values are matched by name WITHIN a step, never globally: "Olive Green" is
 *   a value of both options on the two-tone pendant.
 *
 * Degrades safely: with the blob missing, malformed, or no valid combination
 * found, it returns and Horizon behaves exactly as it did before.
 */
(() => {
  if (window.__sv3Pairs) return;
  window.__sv3Pairs = true;

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      const picker = input.closest('variant-picker');
      if (!picker) return;

      const blob = picker.querySelector('script[data-sv3-combos]');
      if (!blob) return;

      let combos;
      try {
        combos = JSON.parse(blob.textContent || '[]');
      } catch {
        return;
      }
      if (!Array.isArray(combos) || combos.length === 0) return;

      const width = combos[0].o.length;

      /** @type {Map<number, HTMLInputElement[]>} option position -> its radios */
      const steps = new Map();
      for (const fieldset of picker.querySelectorAll('fieldset')) {
        const radios = /** @type {HTMLInputElement[]} */ ([
          ...fieldset.querySelectorAll('input[type="radio"]'),
        ]);
        if (!radios.length) continue;
        const position = Number(radios[0].dataset.fieldsetIndex);
        if (Number.isNaN(position) || position >= width) continue;
        steps.set(position, radios);
      }

      let changed = -1;
      for (const [position, radios] of steps) {
        if (radios.includes(input)) changed = position;
      }
      if (changed < 0) return;

      const chosen = new Array(width).fill(undefined);
      for (const [position, radios] of steps) {
        chosen[position] = radios.find((r) => r.checked)?.value;
      }
      chosen[changed] = input.value;

      const fits = (combo) =>
        combo.o.every((value, i) => chosen[i] === undefined || value === chosen[i]);

      const exact = combos.find(fits);
      if (exact) {
        input.dataset.variantId = String(exact.id);
        return; // already a real combination — nothing to reconcile
      }

      let best = null;
      let bestScore = -1;
      for (const combo of combos) {
        if (combo.o[changed] !== chosen[changed]) continue;
        let score = 0;
        for (let i = 0; i < width; i++) {
          if (i === changed || chosen[i] === undefined) continue;
          if (combo.o[i] === chosen[i]) score += i < changed ? 1000 : 1;
        }
        if (score > bestScore) {
          bestScore = score;
          best = combo;
        }
      }
      if (!best) return;

      for (const [position, radios] of steps) {
        if (position === changed || best.o[position] === chosen[position]) continue;
        // The radio for a value that is not currently offered is still in the
        // DOM — the picker emits it hidden precisely so this can reach it.
        const radio = radios.find((r) => r.value === best.o[position]);
        if (radio) {
          radio.checked = true;
          radio.dataset.currentChecked = 'true';
        }
      }

      input.dataset.variantId = String(best.id);
    },
    true
  );
})();
